import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DatePicker from '../../../shared/DatePicker'
import CustomButton from '../../../shared/CustomButton'
import moment from 'moment'
import { fetchData } from '../../../helpers/fetchData'
import toast from 'react-hot-toast'
import { FaCheckDouble } from 'react-icons/fa6'
import DropdownField from '../../../shared/DropdownField'
import CustomTable from '../../../shared/CustomTable'
import SwitchInput from '../../../shared/SwitchInput'
import BulkProcessingLogs from './BulkProcessingLogs'
import { payersOptionsFormat } from '../../../constants/BeneficiaryConstants'

function BulkProcessClaimsModal({ onClose, onComplete }) {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    start_date: moment().startOf('month').format('YYYY-MM-DD'),
    end_date: moment().endOf('month').format('YYYY-MM-DD'),
    max_workers: 5,
    auto_submit: false,
    payer_id: '',
    reprocess_existing: false
  })
  const [loading, setLoading] = useState(false)
  const [statsLoading, setStatsLoading] = useState(false)
  const [payerList, setPayerList] = useState(null)
  const [fieldLoading, setFieldLoading] = useState(false)
  const [stats, setStats] = useState(null)
  const [jobId, setJobId] = useState(null)

  const payerStatsColumns = [
    {
      header: t('Payer Name'),
      field: 'payer_name',
      headerCellClass:"!bg-gray-200"
    },
    // {
    //   header: t('Claim Count'),
    //   field: 'claim_count',
    //   body: (row) => <span className='font-medium'>{row.claim_count}</span>,
    //   headerCellClass:"!bg-gray-200"
    // },
    {
      header: t('Total Items'),
      field: 'total_items',
      headerCellClass:"!bg-gray-200"
    },
     {
      header: t('Patient Share'),
      body: (row) => <span>{Number(row.patient_share || 0).toFixed(2)}</span>,
      headerCellClass:"!bg-gray-200"
    },
    {
      header: t('Patient Vat'),
      body: (row) => <span>{Number(row.patient_vat || 0).toFixed(2)}</span>,
      headerCellClass:"!bg-gray-200"
    },
    {
      header: t('Total Discount'),
      body: (row) => <span>{Number(row.total_discount || 0).toFixed(2)}</span>,
      headerCellClass:"!bg-gray-200"
    },
    {
      header: t('Company Share'),
      body: (row) => <span>{Number(row.total_amount_without_vat || 0).toFixed(2)}</span>,
      headerCellClass:"!bg-gray-200"
    },
    {
      header: t('Company Vat'),
      body: (row) => <span>{Number(row.total_vat || 0).toFixed(2)}</span>,
      headerCellClass:"!bg-gray-200"
    },
    {
      header: t('Claim Amount'),
      body: (row) => <span className='font-medium'>{Number(row.total_amount || 0).toFixed(2)}</span>,
      headerCellClass:"!bg-gray-200"
    },
  ]

  const getPayersList = async () => {
    setFieldLoading(true)
    let response = await fetchData('POST', '/v3/payer/get', {})
    response?.data ? setPayerList(response?.data) : setPayerList(null)
    setFieldLoading(false)
  }

  useEffect(() => {
    getPayersList()
  }, [])

  const handleDateChange = (event) => {
    const { name, value } = event.target || {}
    setFormData(prev => ({
      ...prev,
      [name]: moment(value).format('YYYY-MM-DD')
    }))
    setStats(null)
  }

  const handlePayerChange = (e) => {
    setFormData(prev => ({
      ...prev,
      payer_id: e.target?.value
    }))
    setStats(null)
  }

  const handleReprocessChange = (event) => {
    setFormData(prev => ({
      ...prev,
      reprocess_existing: event.value
    }))
    setStats(null)
  }

  const fetchStats = async () => {
    if (!formData.start_date || !formData.end_date) {
      toast.error(t('Please select both start and end dates'))
      return
    }

    if (moment(formData.end_date).isBefore(moment(formData.start_date))) {
      toast.error(t('End date must be after start date'))
      return
    }

    setStatsLoading(true)
    try {
      const payload = {
        start_date: formData.start_date,
        end_date: formData.end_date,
        payer_id: formData.payer_id
      }
      const response = await fetchData('POST', '/v3/invoices/bulkClaimStats', payload)
      if (response) {
        setStats(response)
      }
    } catch (error) {
      console.error('Stats fetch error:', error)
      toast.error(error?.message || t('Failed to fetch statistics'))
    } finally {
      setStatsLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!formData.start_date || !formData.end_date) {
      toast.error(t('Please select both start and end dates'))
      return
    }

    if (moment(formData.end_date).isBefore(moment(formData.start_date))) {
      toast.error(t('End date must be after start date'))
      return
    }

    if (formData.reprocess_existing && !formData.payer_id) {
      toast.error(t('Please select a payer before reprocessing existing claims'))
      return
    }

    setLoading(true)
    try {
      const response = await fetchData('POST', 'v3/invoices/processBulkClaims', formData)
      if (response?.message && !response?.job_id) {
        toast.error(response?.message)
      }
      if (response?.job_id) {
        toast.success(response?.message || t('Bulk processing started successfully'))
        setJobId(response.job_id)
      }
    } catch (error) {
      console.error('Bulk processing error:', error)
      toast.error(error?.message || t('Failed to start bulk processing'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      {jobId ? (
        <BulkProcessingLogs jobId={jobId} onClose={onClose} onComplete={onComplete} />
      ) : (
      <div className='space-y-2'>
        <div className='grid grid-cols-2 lg:grid-cols-3 gap-4'>
          <DatePicker
            label={t('StartDate')}
            name='start_date'
            value={moment(formData.start_date, 'YYYY-MM-DD').toDate()}
            onChange={handleDateChange}
            useFormik={false}
            placeholder={t('StartDate')}
          />
          <DatePicker
            label={t('EndDate')}
            name='end_date'
            value={moment(formData.end_date, 'YYYY-MM-DD').toDate()}
            onChange={handleDateChange}
            useFormik={false}
            placeholder={t('EndDate')}
          />
          <DropdownField
            filter
            useFormik={false}
            options={payersOptionsFormat(payerList)}
            // options={payerList?.map(py => ({
            //   value: py?.payer_id,
            //   label: py?.name
            // }))}
            loading={fieldLoading}
            label={t('Payer')}
            placeholder={t('Select Payer')}
            handleChange={handlePayerChange}
            value={formData?.payer_id}
          />
          <SwitchInput
            label={t('Reprocess Existing')}
            value={formData.reprocess_existing}
            handleChange={handleReprocessChange}
          />
        </div>
        {formData.reprocess_existing && (
          <div className='border border-yellow-200 bg-yellow-50 text-yellow-800 text-sm rounded-md p-3'>
            {t('Reprocessing deletes old generated claim files and records for the selected payer, then regenerates them.')}
          </div>
        )}
        {stats && (
          <div className='border border-gray-200 rounded-md p-2 space-y-3 mt-4'>
            <div className='grid grid-cols-3 lg:grid-cols-6 gap-3'>
              <div className='border border-gray-100 bg-gray-100 p-2 rounded'>
                <p className='text-xs text-gray-600'>{t('Patient Share')}</p>
                <p className='text-md font-semibold text-blue-900'>{Number(stats?.patient_share || 0).toFixed(2)}</p>
              </div>
              <div className='border border-gray-100 bg-gray-100 p-2 rounded'>
                <p className='text-xs text-gray-600'>{t('Patient Vat')}</p>
                <p className='text-md font-semibold text-blue-900'>{Number(stats?.patient_vat || 0).toFixed(2)}</p>
              </div>
              <div className='border border-gray-100 bg-gray-100 p-2 rounded'>
                <p className='text-xs text-gray-600'>{t('Total Discount')}</p>
                <p className='text-md font-semibold'>{Number(stats?.total_discount || 0).toFixed(2)}</p>
              </div>
              <div className='border border-gray-100 bg-gray-100 p-2 rounded'>
                <p className='text-xs text-gray-600'>{t('Company Share')}</p>
                <p className='text-md font-semibold'>{Number(stats?.total_amount_without_vat || 0).toFixed(2)}</p>
              </div>
              <div className='border border-gray-100 bg-gray-100 p-2 rounded'>
                <p className='text-xs text-gray-600'>{t('Company Vat')}</p>
                <p className='text-md font-semibold'>{Number(stats?.total_vat || 0).toFixed(2)}</p>
              </div>
              <div className='border border-gray-100 bg-gray-100 p-2 rounded'>
                <p className='text-xs text-gray-600'>{t('Claim Amount')}</p>
                <p className='text-md font-semibold'>{Number(stats?.total_amount || 0).toFixed(2)}</p>
              </div>
            </div>

            {stats?.payer_stats && stats.payer_stats.length > 0 && (
              <div className='mt-2'>
                <CustomTable
                  data={stats.payer_stats}
                  columns={payerStatsColumns}
                  isLoading={false}
                />
              </div>
            )}
          </div>
        )}
        <div className='flex gap-2 justify-end pt-3'>
          {/* <CustomButton
            label={t('Fetch Data')}
            buttonType='reset'
            onClick={fetchStats}
            loading={statsLoading}
            disabled={statsLoading || loading}
            icon='pi pi-chart-bar'
          />
          {stats && stats?.total_claims > 0 && <CustomButton
            label={t('Start Generation')}
            buttonType='primary'
            onClick={handleSubmit}
            loading={loading}
            disabled={loading || statsLoading}
            icon={<FaCheckDouble size={15} className='me-2' />}
          />} */}
         <CustomButton
            label={t('Start Generation')}
            buttonType='primary'
            onClick={handleSubmit}
            loading={loading}
            disabled={loading || statsLoading}
            icon={<FaCheckDouble size={15} className='me-2' />}
          />

        </div>
      </div>
      )}
    </div>
  )
}

export default BulkProcessClaimsModal
